import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, Grid, Modal, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { branch, compose, lifecycle, renderComponent, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { industryType } from '../../../../constants';
import { focusElement, handleInvalidForm } from '../../../../services';
import { adminActions } from '../../../../state/modules/admin';
import { noCapFilter, noCapMap, pluralise } from '../../../../utils';
import { ControlGroup, ControlOption, FormField, FormItem, Icon, InputFormField, NumberFormField, SelectFormField, ServerError } from '../../../components';
import Loading from '../../loading';

const validate = ({ name, identifier, coreTopicLabel, subTopicLabel }, mappedIndustryCpdCategory) => ({
  mappedIndustryCpdCategory: fp.flow(
    fp.uniqBy(
      ({ cpdCategoryId }) => cpdCategoryId
    ),
    (uniqIndustryCpdCategory) => {
      const isAoaUniq = uniqIndustryCpdCategory.length === mappedIndustryCpdCategory.length
      const isAoaDefined = fp.find(
        ({ cpdCategoryId }) => cpdCategoryId === undefined
      )(mappedIndustryCpdCategory) ? false : true
      return (isAoaUniq && isAoaDefined) ? null : 'CPD Category must be defined and unique'
    }
  )(mappedIndustryCpdCategory),
  name: (name && name.length > 0) ? null : 'Name is required',
  identifier: (identifier && identifier.length > 0) ? null : 'Identifier is required',
  coreTopicLabel: (coreTopicLabel && coreTopicLabel.length > 0) ? null : 'Core topic label is required',
  subTopicLabel: (subTopicLabel && subTopicLabel.length > 0) ? null : 'Sub topic label is required',
});

const isValid = ({ name, identifier, coreTopicLabel, subTopicLabel, mappedIndustryCpdCategory }) =>
  !name && !identifier && !coreTopicLabel && !subTopicLabel && !mappedIndustryCpdCategory;

const getNewIndustry = () => ({
  name: 'new industry',
  defaultCpdPoints: 30,
  defaultCpdDay: 30,
  defaultCpdMonth: 6,
  identifier: 'new-industry',
  industryType: industryType.Total,
  includeTasa: false,
  maxMicroCpdPortion: 1,
  minimumCpdPoints: 30,
  cpdPeriodInMonths: 12,
  coreTopicLabel: 'Core Topics',
  subTopicLabel: 'Sub Topics',
});

const industryFields = {
  name: 'name',
  defaultCpdPoints: 'defaultCpdPoints',
  minimumCpdPoints: 'minimumCpdPoints',
  defaultCpdDay: 'defaultCpdDay',
  defaultCpdMonth: 'defaultCpdMonth',
  capCpdActivity: 'capCpdActivity',
  identifier: 'identifier',
  includeTasa: 'includeTasa',
  industryType: 'industryType',
  maxMicroCpdPortion: 'maxMicroCpdPortion',
  coreTopicLabel: 'coreTopicLabel',
  subTopicLabel: 'subTopicLabel',
};

const formalEducationOptions = {
  capped: 'Capped',
  notCapped: 'Not Capped',
};

const AdminIndustryEdit = ({
  industry,
  updateIndustryField,
  updateIndustry,
  adminError,
  focusError,
  setFocusError,
  mappedIndustryCpdCategory,
  updateIndustryCpdCategory,
  cpdCategorys,
  addCpdCategory,
  removeCpdCategory,
  showConfirmation,
  toggleConfirmation,
  confirmUpdate,
  confirmationMessage,
  isCreating,
  goals
}) => (
    <div id='section--admin-industry-edit'>
      {
        isCreating ?
          <h1 className='text-center'>Create Industry</h1> :
          <h1 className='text-center'>Edit Industry</h1>
      }
      <Grid>
        <Row className='md-gutter'>
          <Col xs={12} md={8} mdOffset={2} className='boxed text-left'>
            <form onSubmit={updateIndustry}>
              {
                adminError ?
                  <ServerError
                    innerRef={(messageRef) => {
                      if (messageRef && focusError) {
                        focusElement(messageRef);
                        setFocusError(false);
                      }
                    }}
                    error={adminError}
                    hideSupport /> : null
              }
              <Row>
                <Col xs={12}>
                  <InputFormField {...{
                    id: industryFields.name,
                    onChange: (e) => updateIndustryField(industryFields.name, e.target.value),
                    validation: industry.validation.name,
                    value: industry.name
                  }} />
                </Col>
              </Row>
              <ControlGroup
                id={industryFields.industryType}
                control='checkbox'
                type='styled'
                label='Industry Type'
                onChange={(e) => updateIndustryField(industryFields.industryType, e.target.value)}>
                <ControlOption value={industryType.Minimum} checked={industry.industryType === industryType.Minimum} label={<span>{industryType.Minimum}</span>} />
                <ControlOption value={industryType.Total} checked={industry.industryType === industryType.Total} label={<span>{industryType.Total}</span>} />
              </ControlGroup>
              <Row>
                <Col xs={12}>
                  <InputFormField {...{
                    id: industryFields.identifier,
                    onChange: (e) => updateIndustryField(industryFields.identifier, e.target.value),
                    validation: industry.validation.identifier,
                    value: industry.identifier
                  }} />
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <FormItem>
                    <label htmlFor={industryFields.defaultCpdPoints}>Default CPD Points</label>
                  </FormItem>
                  <NumberFormField {...{
                    id: industryFields.defaultCpdPoints,
                    label: null,
                    min: 0,
                    onChange: (e) => updateIndustryField(industryFields.defaultCpdPoints, e.value),
                    required: true,
                    value: industry.defaultCpdPoints
                  }} />
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <FormItem>
                    <label htmlFor={industryFields.minimumCpdPoints}>Minimum CPD Points</label>
                  </FormItem>
                  <NumberFormField {...{
                    id: industryFields.minimumCpdPoints,
                    label: null,
                    min: 0,
                    onChange: (e) => updateIndustryField(industryFields.minimumCpdPoints, e.value),
                    required: true,
                    value: industry.minimumCpdPoints
                  }} />
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <FormItem>
                    <label htmlFor={industryFields.defaultCpdMonth}>Default CPD Month</label>
                  </FormItem>
                  <NumberFormField {...{
                    id: industryFields.defaultCpdMonth,
                    label: null,
                    min: 1,
                    max: 12,
                    step: 1,
                    onChange: (e) => updateIndustryField(industryFields.defaultCpdMonth, Math.floor(e.value)),
                    required: true,
                    value: industry.defaultCpdMonth
                  }} />
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <FormItem>
                    <label htmlFor={industryFields.defaultCpdDay}>Default CPD Day</label>
                  </FormItem>
                  <NumberFormField {...{
                    id: industryFields.defaultCpdDay,
                    label: null,
                    min: 1,
                    max: 31,
                    step: 1,
                    onChange: (e) => updateIndustryField(industryFields.defaultCpdDay, Math.floor(e.value)),
                    required: true,
                    value: industry.defaultCpdDay
                  }} />
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <FormItem>
                    <label htmlFor={industryFields.maxMicroCpdPortion}>Max Micro CPD Portion</label>
                  </FormItem>
                  <NumberFormField {...{
                    min: 0,
                    max: 1,
                    step: 0.1,
                    id: industryFields.maxMicroCpdPortion,
                    label: null,
                    onChange: (e) => updateIndustryField(industryFields.maxMicroCpdPortion, parseFloat(e.value.toFixed(2))),
                    required: true,
                    value: industry.maxMicroCpdPortion
                  }} />
                </Col>
              </Row>
              <ControlGroup
                id={industryFields.includeTasa}
                control='checkbox'
                type='styled'
                label='TPB'
                onChange={(e) => updateIndustryField(industryFields.includeTasa, !industry.includeTasa)}>
                <ControlOption checked={industry.includeTasa} label={<span>Include TPB</span>} />
              </ControlGroup>
              <ControlGroup
                id={industryFields.capCpdActivity}
                control='radio'
                type='pill'
                label='Formal Education'
                onChange={(e) => {
                  updateIndustryField(industryFields.capCpdActivity, e.target.value === formalEducationOptions.capped ? 0 : undefined)
                }}>
                <ControlOption value={formalEducationOptions.capped} checked={industry.capCpdActivity !== undefined} type={'radio'} label={<span>Capped</span>} />
                <ControlOption value={formalEducationOptions.notCapped} checked={industry.capCpdActivity === undefined} type={'radio'} label={<span>Not Capped</span>} />
              </ControlGroup>
              {
                industry.capCpdActivity !== undefined ?
                  <Row>
                    <Col xs={12} md={6}>
                      <FormItem>
                        <label htmlFor={industryFields.capCpdActivity}>Cap CPD Activities</label>
                      </FormItem>
                      <NumberFormField {...{
                        id: industryFields.capCpdActivity,
                        label: null,
                        min: 0,
                        onChange: (e) => updateIndustryField(industryFields.capCpdActivity, e.value),
                        required: false,
                        value: industry.capCpdActivity
                      }} />
                    </Col>
                  </Row> : null
              }

              <Row>
                <Col xs={12}>
                  <InputFormField {...{
                    id: industryFields.coreTopicLabel,
                    onChange: (e) => updateIndustryField(industryFields.coreTopicLabel, e.target.value),
                    validation: industry.validation.coreTopicLabel,
                    value: industry.coreTopicLabel
                  }} />
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <InputFormField {...{
                    id: industryFields.subTopicLabel,
                    onChange: (e) => updateIndustryField(industryFields.subTopicLabel, e.target.value),
                    validation: industry.validation.subTopicLabel,
                    value: industry.subTopicLabel
                  }} />
                </Col>
              </Row>

              {
                isCreating ? null :
                  <Row>
                    <Col xs={12}>
                      <FormField
                        id='CpdCategory'
                        lable='CpdCategory'
                        type={null}
                        validation={industry.validation.mappedIndustryCpdCategory}>
                        <Row>
                          {
                            noCapMap(({ cpdCategoryId, parentCpdCategoryId, id }, index) =>
                              <Col key={`${index}-${id}`} xs={12}>
                                <Row className='xs-gutter'>
                                  <Col xs={6}>
                                    <SelectFormField
                                      formGroupProps={{ className: 'clearfix' }}
                                      id={`cpd-category-${id}`}
                                      label='CPD Category'
                                      onChange={(e) => updateIndustryCpdCategory(index, 'cpdCategoryId', parseInt(e.target.value, 10))}
                                      value={cpdCategoryId}
                                      emptyOption={false}>
                                      {
                                        fp.map(({ id, name }) =>
                                          <option key={id} value={id}>
                                            {he.decode(name)}
                                          </option>
                                        )(cpdCategorys)
                                      }
                                    </SelectFormField>
                                  </Col>
                                  <Col xs={5}>
                                    <SelectFormField
                                      formGroupProps={{ className: 'clearfix' }}
                                      id={`cpd-category-${id}`}
                                      label='Parent CPD Category'
                                      onChange={(e) => updateIndustryCpdCategory(index, 'parentCpdCategoryId', parseInt(e.target.value, 10))}
                                      value={parentCpdCategoryId}
                                      emptyOption={true}>
                                      {
                                        fp.flow(
                                          fp.filter(
                                            ({ cpdCategoryId: cid }) => cid !== cpdCategoryId
                                          ),
                                          fp.map(
                                            ({ cpdCategoryId }) => fp.find(
                                              ({ id }) => id === cpdCategoryId
                                            )(cpdCategorys)
                                          ),
                                          fp.filter(
                                            fp.identity
                                          ),
                                          fp.map(({ id, name }) =>
                                            <option key={id} value={id}>
                                              {he.decode(name)}
                                            </option>
                                          )
                                        )(mappedIndustryCpdCategory)
                                      }
                                    </SelectFormField>
                                  </Col>
                                  <Col xs={1}>
                                    <Button
                                      bsStyle='plain'
                                      className='large area-of-advice-remove pull-right'
                                      onClick={() => removeCpdCategory(index)}>
                                      <Icon icon='close-thick' />
                                    </Button>
                                  </Col>
                                </Row>
                              </Col>
                            )(mappedIndustryCpdCategory)
                          }
                          <Col xs={12} className='flex-center'>
                            <Button
                              onClick={addCpdCategory}
                              className='mt-3'>
                              <Icon icon='add-large' /> Add CPD Category
                        </Button>
                          </Col>
                        </Row>
                      </FormField>
                    </Col>
                  </Row>
              }

              <Row className='top-gap'>
                <Col xs={12}>
                  <ControlGroup
                    id='goals'
                    control='checkbox'
                    type='styled'
                    onChange={() => { }}
                    label='Goals'>
                    {
                      fp.map(
                        (goal) => <ControlOption key={goal.id} checked={goal.checked} label={<span>{goal.name}</span>} />
                      )(goals)
                    }
                  </ControlGroup>
                </Col>
              </Row>

              <Row className='top-gap'>
                <Col xs={12}>
                  <Button
                    className='flex-center'
                    type='submit'>
                    {
                      isCreating ? 'Create' : 'Update'
                    }
                  </Button>

                  <Modal show={showConfirmation} onHide={toggleConfirmation}>
                    <Modal.Header>
                      <Modal.Title componentClass='h2' className='text-center'>
                        {`You are going to update industry CPD Category`}
                      </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className='text-center'>
                      <div className='has-error'>
                        <Icon icon='warning-large' iconSize={2} />
                        <span>
                          {`You are about to change the CPD categories. Ensure there are no circular dependencies before continuing.`}
                        </span>
                      </div>
                    </Modal.Body>
                    <Modal.Body className='text-center'>
                      {`
                        Your are goint to:
                      `}
                    </Modal.Body>
                    <Modal.Body className='text-center'>
                      {`
                        add ${fp.get('added')(confirmationMessage)} CPD ${pluralise('category', fp.get('added')(confirmationMessage))},
                      `}
                    </Modal.Body>
                    <Modal.Body className='text-center'>
                      {`
                        remove ${fp.get('deleted')(confirmationMessage)} CPD ${pluralise('category', fp.get('deleted')(confirmationMessage))},
                      `}
                    </Modal.Body>
                    <Modal.Body className='text-center'>
                      {`
                        and modified ${fp.get('modified')(confirmationMessage)} CPD ${pluralise('category', fp.get('modified')(confirmationMessage))}.
                      `}
                    </Modal.Body>
                    <Modal.Body className='text-center'>
                      {`
                        Are you sure you want to do that?
                      `}
                    </Modal.Body>
                    <Modal.Footer className='text-center confirmation-footer'>
                      <Row>
                        <Col xs={12}>
                          <Button onClick={confirmUpdate}>
                            Update
                          </Button>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12}>
                          <Button bsStyle='plain' onClick={toggleConfirmation}>
                            Cancel
                          </Button>
                        </Col>
                      </Row>
                    </Modal.Footer>
                  </Modal>
                </Col>
              </Row>
            </form>
          </Col>
        </Row>
      </Grid>
    </div>
  );

const mapStateToProps = (state) => ({
  industries: state.industry.industriesWithCpdCategorys,
  industryCpdCategory: state.admin.industryCpdCategory,
  cpdCategorys: state.cpdCategorys.cpdCategorys,
  adminError: state.admin.error
});

const mapDispatchToProps = (dispatch) => ({
  updateIndustry: bindActionCreators(adminActions.admin.updateIndustry, dispatch),
  addIndustry: bindActionCreators(adminActions.admin.addIndustry, dispatch)
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  branch(
    ({ industries, industryCpdCategory, cpdCategorys }) => industries.length === 0 || industryCpdCategory.length === 0 || cpdCategorys.length === 0,
    renderComponent(Loading)
  ),
  withProps(
    ({
      industries,
      match: {
        params: { industryId }
      }
    }) => {
      const industry = fp.find(
        ({ industry }) => industry.id === parseInt(industryId, 10)
      )(industries)
      return ({
        industryId,
        industry,
        goals: fp.flow(
          fp.map(
            ({ goals }) => fp.map(
              (goal) => ({
                ...goal
              })
            )(goals)
          ),
          fp.flatten,
          fp.uniqBy('id'),
          fp.map(
            (goal) => ({
              ...goal,
              checked: fp.flow(
                fp.get('goals'),
                fp.map('id'),
                fp.includes(goal.id)
              )(industry)
            })
          )
        )(industries),
        isCreating: fp.isEmpty(industryId)
      })
    }
  ),
  withStateHandlers(
    ({
      industry,
      industryCpdCategory,
      cpdCategorys,
      industryId,
      isCreating
    }) => ({
      focusError: false,
      defaultCpdCategory: {
        industryId: parseInt(industryId, 10),
        minimumCpd: 0,
        cpdCategoryId: fp.get('[0].id')(cpdCategorys),
        mandatory: false
      },
      mappedIndustryCpdCategory: fp.filter(
        (industryCpdCategory) => industryCpdCategory.industryId === parseInt(industryId, 10)
      )(industryCpdCategory),
      industry: {
        ...(
          isCreating ?
            getNewIndustry() :
            fp.get('industry')(industry)
        ),
        validation: {}
      },
      deleted: 0,
      added: 0,
      modified: []
    }),
    {
      setValidation: ({ industry }) => (validation) => ({
        industry: {
          ...industry,
          validation
        }
      }),
      setFocusError: () => (focusError) => ({ focusError }),
      updateIndustryCpdCategory: ({ mappedIndustryCpdCategory, modified }) => (index, fieldName, value) => ({
        mappedIndustryCpdCategory: noCapMap(
          (industryCpdCategory, aoaIndex) => aoaIndex === index ? {
            ...industryCpdCategory,
            [fieldName]: value
          } : industryCpdCategory
        )(mappedIndustryCpdCategory),
        modified: fp.flow(
          fp.get(`[${index}].id`),
          (id) => id ? modified.concat([id]) : modified,
          fp.uniq
        )(mappedIndustryCpdCategory)
      }),
      addCpdCategory: ({ mappedIndustryCpdCategory, defaultCpdCategory, added }) => () => ({
        mappedIndustryCpdCategory: mappedIndustryCpdCategory.concat([defaultCpdCategory]),
        added: added + 1
      }),
      removeCpdCategory: ({ mappedIndustryCpdCategory, deleted, added }) => (index) => ({
        mappedIndustryCpdCategory: noCapFilter(
          (industryCpdCategory, aoaIndex) => aoaIndex !== index
        )(mappedIndustryCpdCategory),
        deleted: fp.get(`[${index}].id`)(mappedIndustryCpdCategory) ? deleted + 1 : deleted,
        added: fp.get(`[${index}].id`)(mappedIndustryCpdCategory) ? added : added - 1
      }),
      updateIndustryField: ({ industry }) => (fieldName, value) => ({
        industry: {
          ...industry,
          [fieldName]: value
        }
      })
    }
  ),
  lifecycle({
    componentDidUpdate(prevProps) {
      if (!prevProps.adminError && this.props.adminError) {
        this.props.setFocusError(true);
      }

      const validation = this.props.industry.validation;
      if (!isValid(validation)) {
        const nextValidation = validate(this.props.industry, this.props.mappedIndustryCpdCategory);
        if (JSON.stringify(nextValidation) !== JSON.stringify(validation)) {
          this.props.setValidation(nextValidation);
        }
      }
    }
  }),
  withStateHandlers(
    {
      showConfirmation: false,
      confirmationMessage: undefined
    },
    {
      toggleConfirmation: ({ showConfirmation }) => (confirmationMessage) => ({
        showConfirmation: !showConfirmation,
        confirmationMessage
      })
    }
  ),
  withHandlers({
    updateIndustry: ({ updateIndustry, addIndustry, industry, setValidation, mappedIndustryCpdCategory, industryCpdCategory, toggleConfirmation, deleted, added, modified, isCreating }) => (e) => {
      e.preventDefault();
      e.stopPropagation();

      const mappedIndustryCpdCategoryUpdated = added > 0 || deleted > 0 || modified.length > 0

      const message = {
        deleted,
        added,
        modified: modified.length
      }

      const validation = validate(industry, mappedIndustryCpdCategory)
      if (isValid(validation)) {
        if (mappedIndustryCpdCategoryUpdated) {
          toggleConfirmation(message)
        } else {
          if (isCreating) {
            addIndustry(industry)
          } else {
            updateIndustry(industry.id, industry, mappedIndustryCpdCategory);
          }
        }
      } else {
        setValidation(validation)
        handleInvalidForm(e.target)
      }

    },
    confirmUpdate: ({ updateIndustry, industry, mappedIndustryCpdCategory }) => (e) => {
      updateIndustry(industry.id, industry, mappedIndustryCpdCategory);
    }
  })
)(AdminIndustryEdit);



// WEBPACK FOOTER //
// ./src/containers/pages/admin-portal/industry-portal/industry-edit.js